import React, { memo, useCallback, useState } from 'react';
import { resolveAssetUrl } from '../../../shared/utils/assetUrl';
import './heroFigure.css';

/**
 * The assembled hero as a "box doll" — the same 6 part boxes, at the same proportions, as the
 * character builder (`inventory/components/CharacterAssemblyPreview.jsx` → PART_LAYOUT,
 * wrap 190×200 in `inventory/styles/character.css`). Used by every mini-game (Arena, Brawler,
 * Tactics, Match-3…) so the player recognises their hero everywhere.
 *
 *   <HeroFigure look={look} size={120} facing={1} pose="walk" />        standalone <svg>
 *   <svg>…<HeroFigureG look={look} size={110} pose="attack" />…</svg>   inside an existing SVG
 *
 * Local coordinates of HeroFigureG: origin = feet centre, y grows DOWN (SVG), the figure is
 * `size` units tall (top of the head at y = −size) and faces +x when facing = 1.
 *
 * Props
 *  look      {HEAD:{imageUrl,rank,character}, BODY, LEFT_HAND, RIGHT_HAND, LEFT_LEG, RIGHT_LEG}
 *            (protocol §11.2). A character DTO (headModule, bodyModule…) is accepted too.
 *            Missing slots / broken images → stylised box in the hero-class colours.
 *  character hero class ('BATMAN'…) or a character DTO; only used for fallback colours
 *  size      figure height in px (HeroFigure) or user units (HeroFigureG), default 110
 *  facing    1 | -1
 *  pose      'idle'|'walk'|'jump'|'windup'|'attack'|'block'|'cast'|'stun'|'ko'
 *  ranks     true → small rank badges on parts with rank > 0
 *  animated  false → no CSS pose animation (static thumbnail)
 *  className extra class on the root
 */

export const SLOTS = ['HEAD', 'BODY', 'LEFT_HAND', 'RIGHT_HAND', 'LEFT_LEG', 'RIGHT_LEG'];

/** Character DTO keys (GET /characters) per slot. */
export const SLOT_KEYS = {
  HEAD: 'headModule',
  BODY: 'bodyModule',
  LEFT_HAND: 'leftHandModule',
  RIGHT_HAND: 'rightHandModule',
  LEFT_LEG: 'leftLegModule',
  RIGHT_LEG: 'rightLegModule',
};

/**
 * Builder wrap: 190 × 200 → doll space 100 × DOLL_H. Every part is a square box of `s` (percent
 * of the wrap width), centred at (cx% of width, cy% of height) — exactly PART_LAYOUT.
 */
export const DOLL_W = 100;
export const DOLL_H = (200 / 190) * 100;
export const BUILDER_LAYOUT = {
  RIGHT_LEG: { left: 65, top: 73, size: 26, z: 1 },
  LEFT_LEG: { left: 33, top: 73, size: 26, z: 1 },
  BODY: { left: 50, top: 41, size: 46, z: 2 },
  RIGHT_HAND: { left: 76, top: 50, size: 26, z: 3 },
  LEFT_HAND: { left: 26, top: 50, size: 26, z: 3 },
  HEAD: { left: 50, top: 17, size: 48, z: 4 },
};

/**
 * Height / width of the part art. The builder sizes parts by WIDTH (img height:auto), and arms / legs are tall
 * images (280×560, 280×467) — squeezing them into a square box shrank the limbs and opened a gap between the
 * body and the legs. Admin uploads follow the same templates.
 */
export const SLOT_ASPECT = { HEAD: 1, BODY: 1, LEFT_HAND: 2, RIGHT_HAND: 2, LEFT_LEG: 467 / 280, RIGHT_LEG: 467 / 280 };

/**
 * Part boxes in doll units: {x, y, s} = square box (fallback art, badges, transform origin),
 * {iy, ih} = top and height of the image itself (width s, height s × aspect), both centred like the builder.
 */
export const PART_BOXES = Object.fromEntries(Object.entries(BUILDER_LAYOUT).map(([slot, l]) => {
  const s = (l.size / 100) * DOLL_W;
  const cx = (l.left / 100) * DOLL_W;
  const cy = (l.top / 100) * DOLL_H;
  const ih = s * SLOT_ASPECT[slot];
  return [slot, { x: cx - s / 2, y: cy - s / 2, s, iy: cy - ih / 2, ih, z: l.z }];
}));

const TOP = Math.min(...Object.values(PART_BOXES).map((b) => Math.min(b.y, b.iy))); // head top (< 0)
const BOTTOM = Math.max(...Object.values(PART_BOXES).map((b) => Math.max(b.y + b.s, b.iy + b.ih))); // feet
/** Visual height of the doll in doll units (head top → feet). */
export const DOLL_VISUAL_H = BOTTOM - TOP;
const CENTER_X = 50;

/** Back → front, as in the builder (legs, body, hands, head). The facing-side hand is "front". */
const DRAW_ORDER = ['LEFT_LEG', 'RIGHT_LEG', 'LEFT_HAND', 'BODY', 'RIGHT_HAND', 'HEAD'];

const PART_CLASS = {
  HEAD: 'hf-part hf-head',
  BODY: 'hf-part hf-body',
  LEFT_HAND: 'hf-part hf-hand hf-hand--back',
  RIGHT_HAND: 'hf-part hf-hand hf-hand--front',
  LEFT_LEG: 'hf-part hf-leg hf-leg--back',
  RIGHT_LEG: 'hf-part hf-leg hf-leg--front',
};

export const POSES = ['idle', 'walk', 'jump', 'windup', 'attack', 'block', 'cast', 'stun', 'ko'];

/** Fallback colours per hero class (no / broken part images). */
const PALETTES = {
  BATMAN: { skin: '#2b2b33', suit: '#3a3a46', accent: '#ffd84a' },
  CAPTAIN: { skin: '#f1c6a0', suit: '#2c5ad6', accent: '#e63946' },
  SPIDER: { skin: '#d6283c', suit: '#d6283c', accent: '#2457c5' },
  TURTLE: { skin: '#58b347', suit: '#3e8a33', accent: '#c58b3a' },
  WOLVERINE: { skin: '#f1c6a0', suit: '#f2c230', accent: '#2457c5' },
  STORMTROOPER: { skin: '#f4f4f4', suit: '#e9e9ee', accent: '#1b1b1f' },
  EAGLE: { skin: '#f6f1e6', suit: '#7a4b2a', accent: '#ffb547' },
  MONKEY: { skin: '#c99568', suit: '#7b4a2b', accent: '#ffe14a' },
  thug: { skin: '#b58867', suit: '#5b5470', accent: '#ff588b' },
  brute: { skin: '#8f6a52', suit: '#6b2f2f', accent: '#ffb547' },
  boss: { skin: '#5a3d6b', suit: '#2a1636', accent: '#ff3d3d' },
  DEFAULT: { skin: '#f1c6a0', suit: '#693de9', accent: '#cfff1f' },
};

export function heroPalette(character) {
  const key = typeof character === 'string' ? character : '';
  return PALETTES[key] || PALETTES[key.toUpperCase()] || PALETTES.DEFAULT;
}

/** Character DTO (headModule, …) → protocol look; a look passes through; otherwise null. */
export function normalizeLook(look) {
  if (!look || typeof look !== 'object') return null;
  if (SLOTS.some((s) => look[s])) return look;
  if (SLOT_KEYS.HEAD in look || SLOT_KEYS.BODY in look || 'headImageUrl' in look) {
    const out = {};
    SLOTS.forEach((slot) => {
      const m = look[SLOT_KEYS[slot]];
      if (m && m.imageUrl) out[slot] = { imageUrl: m.imageUrl, rank: m.rank ?? 0, character: m.character ?? null };
    });
    if (!out.HEAD && look.headImageUrl) out.HEAD = { imageUrl: look.headImageUrl, rank: 0, character: look.character ?? null };
    return Object.keys(out).length ? out : null;
  }
  return null;
}

/** Most frequent `character` among the parts (head wins ties) — the hero class. */
export function lookCharacter(look) {
  const l = normalizeLook(look);
  if (!l) return null;
  const counts = new Map();
  SLOTS.forEach((s) => {
    const c = l[s]?.character;
    if (c) counts.set(c, (counts.get(c) || 0) + 1);
  });
  let best = l.HEAD?.character || null;
  let bestN = best ? counts.get(best) : 0;
  counts.forEach((n, c) => { if (n > bestN) { best = c; bestN = n; } });
  return best;
}

function characterKey(character, look) {
  if (typeof character === 'string') return character;
  if (character && typeof character === 'object' && character.character) return character.character;
  return lookCharacter(look) || '';
}

function FallbackPart({ slot, box, pal }) {
  const { x, y, s } = box;
  const sw = 2.4;
  switch (slot) {
    case 'HEAD': {
      const inset = s * 0.16;
      const hs = s - inset * 2;
      return (
        <g className="hf-fallback">
          <rect x={x + inset} y={y + inset} width={hs} height={hs} rx={hs * 0.3} fill={pal.skin} stroke="#000" strokeWidth={sw} />
          <rect x={x + inset} y={y + inset + hs * 0.3} width={hs} height={hs * 0.2} fill={pal.accent} stroke="#000" strokeWidth={sw * 0.6} />
          <rect x={x + s * 0.56} y={y + inset + hs * 0.34} width={hs * 0.16} height={hs * 0.12} rx="1.5" fill="#fff" />
          <path d={`M${x + s * 0.5} ${y + inset + hs * 0.74} q${hs * 0.12} ${hs * 0.08} ${hs * 0.24} 0`} stroke="#000" strokeWidth={sw * 0.7} fill="none" />
        </g>
      );
    }
    case 'BODY': {
      const ix = s * 0.14;
      return (
        <g className="hf-fallback">
          <rect x={x + ix} y={y + s * 0.12} width={s - ix * 2} height={s * 0.8} rx={s * 0.16} fill={pal.suit} stroke="#000" strokeWidth={sw} />
          <path d={`M${x + s * 0.34} ${y + s * 0.3} L${x + s * 0.5} ${y + s * 0.46} L${x + s * 0.66} ${y + s * 0.3}`} stroke={pal.accent} strokeWidth="4" fill="none" strokeLinejoin="round" />
          <rect x={x + ix} y={y + s * 0.72} width={s - ix * 2} height={s * 0.09} fill={pal.accent} stroke="#000" strokeWidth={sw * 0.6} />
        </g>
      );
    }
    case 'LEFT_HAND':
    case 'RIGHT_HAND':
      return (
        <g className="hf-fallback">
          <rect x={x + s * 0.3} y={y + s * 0.06} width={s * 0.4} height={s * 0.66} rx={s * 0.18} fill={pal.suit} stroke="#000" strokeWidth={sw} />
          <rect x={x + s * 0.24} y={y + s * 0.62} width={s * 0.52} height={s * 0.34} rx={s * 0.14} fill={pal.skin} stroke="#000" strokeWidth={sw} />
        </g>
      );
    default: // legs
      return (
        <g className="hf-fallback">
          <rect x={x + s * 0.28} y={y + s * 0.02} width={s * 0.44} height={s * 0.7} rx={s * 0.12} fill={pal.suit} stroke="#000" strokeWidth={sw} />
          <rect x={x + s * 0.18} y={y + s * 0.68} width={s * 0.64} height={s * 0.3} rx={s * 0.1} fill="#1d1640" stroke="#000" strokeWidth={sw} />
        </g>
      );
  }
}

function RankBadge({ box, rank }) {
  const r = 7;
  const cx = box.x + box.s - r * 0.6;
  const cy = box.y + r * 0.6;
  return (
    <g className="hf-rank" data-rank={rank}>
      <circle cx={cx} cy={cy} r={r} fill="#ffd84a" stroke="#000" strokeWidth="1.8" />
      <text x={cx} y={cy + 3.4} textAnchor="middle" fontSize="9.5" fontWeight="900" fill="#000">{rank}</text>
    </g>
  );
}

/**
 * The hero as an SVG <g> (for use inside an existing SVG). See the module comment for props.
 */
export const HeroFigureG = memo(function HeroFigureG({
  look, character, size = 110, facing = 1, pose = 'idle', ranks = false, animated = true, className = '',
}) {
  const [broken, setBroken] = useState(() => new Set());
  const onError = useCallback((slot) => () => {
    setBroken((prev) => (prev.has(slot) ? prev : new Set([...prev, slot])));
  }, []);

  const parts = normalizeLook(look) || (character && typeof character === 'object' ? normalizeLook(character) : null);
  const pal = heroPalette(characterKey(character, parts));
  const k = (Number(size) || 110) / DOLL_VISUAL_H;
  const flip = Number(facing) < 0 ? -1 : 1;
  const safePose = POSES.includes(pose) ? pose : 'idle';
  const hasLook = !!parts;

  return (
    <g
      className={`hf hf--${safePose}${animated ? '' : ' hf--static'}${hasLook ? '' : ' hf--fallback'} ${className}`.trim()}
      data-pose={safePose}
      data-look={hasLook ? 'parts' : 'fallback'}
    >
      <g transform={`scale(${flip} 1)`}>
        <g transform={`scale(${k}) translate(${-CENTER_X} ${-BOTTOM})`}>
          <g className="hf-rig">
            {DRAW_ORDER.map((slot) => {
              const box = PART_BOXES[slot];
              const part = parts?.[slot];
              const url = part?.imageUrl && !broken.has(slot) ? resolveAssetUrl(part.imageUrl) : null;
              return (
                <g key={slot} className={PART_CLASS[slot]} data-slot={slot}>
                  {/* the transparent box pins fill-box transform origins to the builder box */}
                  <rect x={box.x} y={box.iy} width={box.s} height={box.ih} fill="none" className="hf-box" />
                  {url ? (
                    <image
                      href={url}
                      x={box.x}
                      y={box.iy}
                      width={box.s}
                      height={box.ih}
                      preserveAspectRatio="xMidYMid meet"
                      onError={onError(slot)}
                    />
                  ) : (
                    <FallbackPart slot={slot} box={box} pal={pal} />
                  )}
                  {ranks && Number(part?.rank) > 0 && <RankBadge box={box} rank={Number(part.rank)} />}
                </g>
              );
            })}
          </g>
        </g>
      </g>
    </g>
  );
});

/** Standalone <svg> hero (width = 0.9·size, height = 1.1·size; feet near the bottom). */
function HeroFigure({
  look, character, size = 110, facing = 1, pose = 'idle', ranks = false, animated = true, className = '', title,
}) {
  const h = Number(size) || 110;
  const vbW = h * 0.9;
  const vbH = h * 1.12;
  return (
    <svg
      className={`hero-figure ${className}`.trim()}
      width={vbW}
      height={vbH}
      viewBox={`${-vbW / 2} ${-h * 1.06} ${vbW} ${vbH}`}
      role="img"
      aria-label={title || 'Hero'}
    >
      <HeroFigureG look={look} character={character} size={h} facing={facing} pose={pose} ranks={ranks} animated={animated} />
    </svg>
  );
}

export default memo(HeroFigure);
